import { FormikErrors, FormikProps, FormikTouched } from "formik";
import type { PatientProcedureCode } from "../../../../../services";
import { toTwoDecimals } from "../../../../../utils/utils";
import { MODIFIER_KEYS } from "../common-components/constant";
import type { ProcedureRowErrorKey } from "./claims-constants";

const isBranch = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

/**
 * Walks the value tree and the error tree together and marks every field it
 * finds as touched. A branch only stays a branch while both sides still have
 * keys to walk into: an unselected dropdown holds `{}` and a field that only
 * exists in the errors holds nothing at all, and both have to come out as a
 * plain `true` — that is what the fields check before showing their message.
 */
const markTouched = (...nodes: unknown[]): unknown => {
  const present = nodes.filter((node) => node !== undefined);
  const branches = present.filter(isBranch);
  // a leaf on either side — a plain value, or the error message itself
  if (!branches.length || branches.length !== present.length) return true;

  if (branches.some(Array.isArray)) {
    const length = Math.max(
      ...branches.map((node) => (Array.isArray(node) ? node.length : 0))
    );
    if (!length) return true;
    return Array.from({ length }, (_, index) =>
      markTouched(
        ...branches.map((node) =>
          Array.isArray(node) ? node[index] : undefined
        )
      )
    );
  }

  const keys = new Set(branches.flatMap((node) => Object.keys(node)));
  if (!keys.size) return true;
  return Object.fromEntries(
    [...keys].map((key) => [
      key,
      markTouched(...branches.map((node) => node[key])),
    ])
  );
};

/** Every field of a Formik value tree — and of its errors — marked as touched. */
export const getAllTouched = <T>(
  values: T,
  errors?: FormikErrors<T>
): FormikTouched<T> => markTouched(values, errors) as FormikTouched<T>;

/**
 * Touches every field and re-runs the schema, so a missing value is flagged in
 * place (asterisk + red border + its own message) instead of only surfacing in
 * the banner. Resolves with the resulting errors.
 *
 * Validation runs first so the errors can drive the touched tree too — object
 * fields such as Service Location and Rendering Provider hold `{}` until one is
 * picked, and marking those touched off the values alone left them without a
 * message until the field was focused and blurred.
 */
export const touchAndValidate = async <T>(
  formik: Pick<FormikProps<T>, "values" | "setTouched" | "validateForm">
): Promise<FormikErrors<T>> => {
  const errors = await formik.validateForm();
  await formik.setTouched(getAllTouched(formik.values, errors));
  return errors;
};

/**
 * A CPT line's message, surfaced only once the row has been marked touched
 * (Save / Save & Submit does that for every line), so a freshly opened claim is
 * not covered in red before the user has done anything.
 */
export const getProcedureRowError = (
  formik: Pick<FormikProps<PatientProcedureCode[]>, "touched" | "errors">,
  index: number,
  key: ProcedureRowErrorKey
): string | undefined => {
  const rowState = (formik.touched as Record<string, unknown>[] | undefined)?.[
    index
  ];
  if (!rowState?.[key]) return undefined;
  const message = (formik.errors as Record<string, unknown>[] | undefined)?.[
    index
  ]?.[key];
  return typeof message === "string" ? message : undefined;
};

/**
 * Modifiers arrive either as an array of `{ value, label }` (fee-schedule or
 * manual selection) or as a keyed object; reduce both to the selected values.
 */
export const getSelectedModifiers = (modifiers: unknown): string[] => {
  const list = Array.isArray(modifiers)
    ? modifiers
    : Object.values((modifiers ?? {}) as Record<string, unknown>);
  return list
    .map((mod) =>
      typeof mod === "string" ? mod : (mod as { value?: string })?.value
    )
    .filter((value): value is string => !!value);
};

export const hasAnyDiagnosisPointer = (
  row: Record<string, unknown> | undefined
): boolean => MODIFIER_KEYS.some((key) => !!row?.[key]);

/**
 * A charge arriving from the API (125) shown the way the field itself formats
 * it on blur (125.00), so a populated row does not change under the user the
 * first time they tab through it. Blank / zero charges are left untouched.
 */
export const formatChargeAmount = (charges?: number | string | null) =>
  charges ? toTwoDecimals(charges) : charges;

/**
 * A provider NPI typed into the claim form, shaped the way the Claim record
 * carries it. The form holds the digits as a string, while the claim's
 * `...NPI` fields are numeric, so an unset / partially typed value has to come
 * out as `null` rather than `NaN` or an empty string.
 */
export const toClaimNpi = (npi?: string | number | null): number | null => {
  const digits = String(npi ?? "").trim();
  if (!digits) return null;
  const parsed = Number(digits);
  return Number.isFinite(parsed) ? parsed : null;
};
